import React, { useEffect, useRef, useState } from 'react';
import { partners as mockPartners } from '../data/mock';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PartnersSection = () => {
  const scrollRef = useRef(null);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch partners from API
  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const response = await fetch(`${API_URL}/api/partners`);
        if (response.ok) {
          const data = await response.json();
          // Use API data if available, otherwise use mock data
          setPartners(data.length > 0 ? data : mockPartners);
        } else {
          setPartners(mockPartners);
        }
      } catch (error) {
        console.error('Error fetching partners:', error);
        setPartners(mockPartners);
      } finally {
        setLoading(false);
      }
    };
    fetchPartners();
  }, []);

  useEffect(() => {
    if (loading || partners.length === 0) return;
    
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    let animationId;
    let scrollPosition = 0;
    const scrollSpeed = 0.6;

    const animate = () => {
      scrollPosition += scrollSpeed;
      
      if (scrollPosition >= scrollContainer.scrollWidth / 2) {
        scrollPosition = 0;
      }
      
      scrollContainer.scrollLeft = scrollPosition;
      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);

    const handleMouseEnter = () => {
      cancelAnimationFrame(animationId);
    };

    const handleMouseLeave = () => {
      animationId = requestAnimationFrame(animate);
    };

    scrollContainer.addEventListener('mouseenter', handleMouseEnter);
    scrollContainer.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      cancelAnimationFrame(animationId);
      scrollContainer.removeEventListener('mouseenter', handleMouseEnter);
      scrollContainer.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [loading, partners]);

  const duplicatedPartners = [...partners, ...partners];

  // Couleurs sophistiquées pour les logos
  const getPartnerStyle = (index) => {
    const styles = [
      { bg: 'bg-gradient-to-br from-blue-600 to-blue-800', text: 'text-white' },
      { bg: 'bg-gradient-to-br from-amber-500 to-amber-700', text: 'text-white' },
      { bg: 'bg-gradient-to-br from-stone-700 to-stone-900', text: 'text-white' },
      { bg: 'bg-gradient-to-br from-emerald-600 to-emerald-800', text: 'text-white' },
      { bg: 'bg-gradient-to-br from-rose-500 to-rose-700', text: 'text-white' },
      { bg: 'bg-gradient-to-br from-violet-600 to-violet-800', text: 'text-white' },
      { bg: 'bg-gradient-to-br from-cyan-600 to-cyan-800', text: 'text-white' },
      { bg: 'bg-gradient-to-br from-orange-500 to-orange-700', text: 'text-white' },
      { bg: 'bg-gradient-to-br from-indigo-600 to-indigo-800', text: 'text-white' },
      { bg: 'bg-gradient-to-br from-teal-600 to-teal-800', text: 'text-white' },
    ];
    return styles[index % styles.length];
  };

  return (
    <section id="partenaires" className="py-20 md:py-28 bg-gradient-to-b from-stone-50 to-stone-100 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 mb-12">
        <div className="text-center">
          <p className="text-stone-500 text-sm tracking-[0.25em] uppercase mb-3">
            Nos partenaires
          </p>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-stone-800 font-light">
            Ils nous font confiance
          </h2>
          <div className="w-16 h-px bg-stone-300 mx-auto mt-6" />
          <p className="text-stone-600 max-w-xl mx-auto mt-6 leading-relaxed">
            Nous accompagnons les plus grands promoteurs immobiliers nationaux
            dans la réalisation de leurs programmes de construction.
          </p>
        </div>
      </div>

      {/* Bandeau coulissant */}
      <div className="relative">
        <div className="absolute left-0 top-0 bottom-0 w-24 md:w-48 bg-gradient-to-r from-stone-100 to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-24 md:w-48 bg-gradient-to-l from-stone-100 to-transparent z-10 pointer-events-none" />
        
        <div
          ref={scrollRef}
          className="flex gap-6 overflow-x-hidden py-8"
          style={{ scrollBehavior: 'auto' }}
        >
          {duplicatedPartners.map((partner, index) => {
            const style = getPartnerStyle(index);
            const hasLogo = partner.logo_url && (partner.logo_url.startsWith('/api') || partner.logo_url.startsWith('http'));
            const logoSrc = partner.logo_url?.startsWith('/api') ? `${API_URL}${partner.logo_url}` : partner.logo_url;
            return (
              <div
                key={`${partner.id}-${index}`}
                className="flex-shrink-0 group"
              >
                <div className="w-64 h-24 bg-white rounded-xl shadow-md border border-stone-200 flex items-center gap-4 px-5 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer">
                  {hasLogo ? (
                    <div className="w-14 h-14 rounded-lg bg-white flex items-center justify-center flex-shrink-0 border border-stone-100 overflow-hidden">
                      <img 
                        src={logoSrc} 
                        alt={partner.name}
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                  ) : (
                    <div className={`w-14 h-14 rounded-lg ${style.bg} flex items-center justify-center flex-shrink-0 shadow-sm`}>
                      <span className={`font-bold text-lg ${style.text}`}>
                        {partner.name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-stone-800 text-sm leading-tight truncate">
                      {partner.name}
                    </p>
                    <p className="text-xs text-stone-500 mt-1">
                      Promoteur immobilier
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PartnersSection;
